import { isPresent } from "@apl-digital/utils";
import { createSelector } from "@reduxjs/toolkit";

import type { DiningOption } from "@/api/interfaces/configurationLayouts";
import { hasData } from "@/store/apiRequestStatusMachine";
import {
  selectDefaultDiningOption,
  selectDiningOption,
  selectIsPreOrderAvailable,
} from "@/store/salesPoint";
import type { RootState } from "@/store/slices";
import { EMPTY_ARRAY } from "@/store/utils";

import {
  selectShoppingCart,
  selectShoppingCartLines,
  selectShoppingCartSums,
} from "./slice";
import type { UnifiedCartLine } from "./types";

export const selectShoppingCartProducts = createSelector(
  [selectShoppingCartLines, (_state, productIds?: number[]) => productIds],
  (shoppingCartLines, productIds) => {
    const lines = shoppingCartLines || EMPTY_ARRAY;

    if (productIds) {
      return lines.filter((line) => productIds.includes(line.productId));
    }

    return lines;
  },
);

export const selectShoppingCartUnifiedProducts = createSelector(
  [selectShoppingCartProducts],
  (productCartLines): UnifiedCartLine[] => {
    if (productCartLines.length === 0) {
      return EMPTY_ARRAY;
    }

    const sorted = productCartLines.toSorted((a) => {
      if (a.isSurchargeItem || a.isPackagingItem) {
        return 1;
      }

      return -1;
    });

    return sorted;
  },
);

export const selectShoppingCartTotalItemCount = createSelector(
  [
    selectShoppingCartLines,
    (_state, excludedProductIds: number[] = EMPTY_ARRAY) => excludedProductIds,
  ],
  (lines, excludedProductIds) => {
    if (!isPresent(lines)) {
      return 0;
    }

    return lines.filter((line) => !excludedProductIds.includes(line.productId))
      .length;
  },
);

export const selectShoppingCartTotal = (state: RootState) =>
  selectShoppingCartSums(state)?.LineGrossSum || 0;

export const selectCurrentDiningOption = (
  state: RootState,
): DiningOption | null => {
  const shoppingCart = selectShoppingCart(state);

  if (
    hasData(shoppingCart) &&
    isPresent(shoppingCart.state.Defaults?.DiningOptionID)
  ) {
    const shoppingCartDiningOption = selectDiningOption(
      state,
      shoppingCart.state.Defaults.DiningOptionID,
    );

    if (isPresent(shoppingCartDiningOption)) {
      return shoppingCartDiningOption;
    }
  }

  const defaultDiningOption = selectDefaultDiningOption(state);

  if (isPresent(defaultDiningOption)) {
    return defaultDiningOption;
  }

  return null;
};

export const selectShoppingCartDingingOption = (state: RootState) => {
  const shoppingCart = selectShoppingCart(state);

  if (hasData(shoppingCart)) {
    return shoppingCart.state.Defaults?.DiningOptionID;
  }

  return null;
};

export const selectIsShoppingCartPreOrderAvailable = (state: RootState) => {
  const shoppingCartDiningOption = selectShoppingCartDingingOption(state);

  if (!isPresent(shoppingCartDiningOption)) {
    return false;
  }

  return selectIsPreOrderAvailable(state, shoppingCartDiningOption);
};

export const selectShoppingCartPreOrderDeliveryData = createSelector(
  selectShoppingCart,
  (shoppingCart) => {
    if (!hasData(shoppingCart)) {
      return null;
    }

    if (isPresent(shoppingCart.state.Defaults?.dttmRequestedDelivery)) {
      return new Date(shoppingCart.state.Defaults.dttmRequestedDelivery);
    }

    return null;
  },
);

export const selectShoppingCartItemCount = createSelector(
  [selectShoppingCartLines],
  (shoppingCartLines): number => {
    if (!isPresent(shoppingCartLines)) {
      return 0;
    }

    return shoppingCartLines.reduce((acc, i) => acc + i.amount, 0);
  },
);

export const selectShoppingCartItemCountByProductIds = createSelector(
  [selectShoppingCartLines, (_state, productIds: number[]) => productIds],
  (shoppingCartLines, productIds): number => {
    if (!isPresent(shoppingCartLines)) {
      return 0;
    }

    return shoppingCartLines
      .filter((i) => productIds.includes(i.productId))
      .reduce((acc, i) => acc + i.amount, 0);
  },
);
